import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Head component that dynamically updates meta tags and structured data
 * Based on Surgeboom AEO best practices
 * 
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string} keywords - Meta keywords
 * @param {string} image - OG/Twitter image URL
 * @param {string} type - Open Graph type (website, article, etc.)
 * @param {Object|Array} schema - Schema.org structured data (single object or array)
 * @param {string} author - Article author name
 * @param {string} publishedTime - ISO 8601 date string for article:published_time
 * @param {string} modifiedTime - ISO 8601 date string for article:modified_time
 * @param {Array} breadcrumbs - Array of {name, url} objects for breadcrumb schema
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  image,
  type = 'website',
  schema,
  author,
  publishedTime,
  modifiedTime,
  breadcrumbs
}) => {
  const location = useLocation();
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';
  const currentUrl = `${siteUrl}${location.pathname}`;
  const defaultImage = `${siteUrl}/favicon.png`;

  useEffect(() => {
    // Update document title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name, content, isProperty = false) => {
      if (!content) return;
      const attribute = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Remove meta tag if content is not provided
    const removeMetaTag = (name, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      const meta = document.querySelector(`meta[${attribute}="${name}"]`);
      if (meta) {
        meta.remove();
      }
    };

    // Basic meta tags
    if (description) {
      updateMetaTag('description', description);
      updateMetaTag('og:description', description, true);
      updateMetaTag('twitter:description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Author meta tags
    if (author) {
      updateMetaTag('author', author);
      if (type === 'article') {
        updateMetaTag('article:author', author, true);
      }
    } else if (type === 'article') {
      removeMetaTag('article:author', true);
    }

    // Article date meta tags
    if (publishedTime && type === 'article') {
      updateMetaTag('article:published_time', publishedTime, true);
    } else {
      removeMetaTag('article:published_time', true);
    }

    if (modifiedTime && type === 'article') {
      updateMetaTag('article:modified_time', modifiedTime, true);
    } else {
      removeMetaTag('article:modified_time', true);
    }

    // Open Graph tags
    updateMetaTag('og:title', title || 'DonorsHouse - Connecting Donors Worldwide', true);
    updateMetaTag('og:type', type, true);
    updateMetaTag('og:url', currentUrl, true);
    updateMetaTag('og:image', image || defaultImage, true);
    updateMetaTag('og:site_name', 'DonorsHouse', true);
    updateMetaTag('og:locale', 'en_US', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title || 'DonorsHouse - Connecting Donors Worldwide');
    updateMetaTag('twitter:image', image || defaultImage);
    updateMetaTag('twitter:creator', '@donorshouse');

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', currentUrl);

    // Build schema array
    const schemasToAdd = [];
    
    // Add breadcrumbs schema if provided
    if (breadcrumbs && Array.isArray(breadcrumbs) && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((crumb, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": crumb.name,
          "item": crumb.url
        }))
      };
      schemasToAdd.push(breadcrumbSchema);
    }

    // Add provided schema(s)
    if (schema) {
      if (Array.isArray(schema)) {
        schemasToAdd.push(...schema);
      } else {
        schemasToAdd.push(schema);
      }
    }

    // Remove all existing schema scripts
    const existingSchemas = document.querySelectorAll('script[type="application/ld+json"]');
    existingSchemas.forEach(script => {
      // Don't remove the base organization schema from index.html
      const scriptText = script.textContent || '';
      if (!scriptText.includes('"@type": "Organization"') || schemasToAdd.length === 0) {
        script.remove();
      }
    });

    // Add new schema scripts
    schemasToAdd.forEach((schemaData) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.text = JSON.stringify(schemaData);
      document.head.appendChild(script);
    });
  }, [title, description, keywords, image, type, schema, author, publishedTime, modifiedTime, breadcrumbs, currentUrl, defaultImage]);

  return null;
};

export default SEOHead;

